import { useMemo } from 'react';
import { useApiQuery } from '@locus-taxy/encore-ui-network';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import type { ApiError } from '@locus-taxy/encore-ui-network';

export interface DocumentInfo {
  fileId: string;
  fileName?: string;
  fileType?: string;
  contentType?: string;
  storageMetadata?: {
    url?: string;
    bucketName?: string;
    key?: string;
    provider?: 'S3' | 'EXTERNAL';
  };
}

export interface ProfileInformation {
  photo?: DocumentInfo;
  phoneNumber?: string;
  emailAddress?: string;
  dateOfBirth?: string;
  gender?: string;
}

export interface UserPreferences {
  localeIdentifier: string;
  timezone: string;
}

export interface PersonnelEntityData {
  id: string;
  name: string;
  /**
   * Human-readable role title — "Hub Manager", "Engineer".
   *
   * Distinct from {@link roleId}, which is the machine identifier ("admin").
   * The two are not interchangeable in UI copy: `roleId` is not a label and
   * renders as shouty gibberish next to a person's name. `useProfilePage`
   * reads this same field, including on the path where the entity-details
   * call is skipped and `/me` is the only source.
   */
  role?: string;
  roleId?: string;
  status: string;
  profileInformation?: ProfileInformation;
  skillIds?: string[];
  preferences?: UserPreferences;
  localeSettings?: Record<string, unknown>;
  organizationId?: { id: string };
  /**
   * Pre-record-access "can see every team" flag. Superseded by
   * `accessControl.dimensions[].allAccess`, but still the only signal a backend
   * that predates NBAC sends — so the legacy fallback below reads it.
   */
  allTeamsAccess?: boolean;
}

/**
 * One record-access dimension as reported by `/me` (DSCO-5543, HLD v0.3.1 §A).
 *
 * Phase 1 ships exactly one entry, or none when enforcement is disabled
 * (`record-access.dimensions: []`). Read it through {@link useCurrentUser}'s
 * `activeDimension` rather than indexing `dimensions` at a call site.
 */
export interface AccessDimension {
  /**
   * `'team'` | `'node'` | a future dimension identifier. Doubles as the search
   * entity name: `POST .../entities/{name}/search`.
   */
  name: string;
  /** Array property on records carrying the tags — `'teamIds'` | `'nodeIds'`. */
  recordTagField: string;
  /** The `all-{label} access` override for this actor. */
  allAccess: boolean;
}

export interface MeResponse {
  data: {
    /**
     * Absent on some responses — never assume it is present (HLD v0.3.1 §A).
     *
     * The actor's own id lives **here**, as `entityData.id` — there is no
     * `data.id` sibling. Read it through {@link useCurrentUser}'s `userId`
     * rather than reaching in at a call site.
     */
    entityData?: PersonnelEntityData;
    /**
     * Absent on pre-record-access BE builds; present with an empty `dimensions`
     * when a deployment has enforcement switched off.
     *
     * These two are **not** the same and must not be collapsed. Absent means
     * "this backend cannot tell us", and we fall back to the legacy team
     * dimension so an un-upgraded client keeps the filtering it already had.
     * Present-but-empty means "this backend told us: none", which is an answer,
     * and falling back there would re-enable filtering an operator turned off.
     */
    accessControl?: {
      dimensions: AccessDimension[];
    };
    schemaInfo: {
      schemaVersion: string;
    };
    metadata: {
      processInstanceKey: string;
      rootInstanceKey: string;
    };
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
    deleted: boolean;
  };
  success: boolean;
}

/**
 * Current user, plus the deployment's active record-access dimension.
 *
 * `activeDimension` is the single accessor for the record-access contract — no
 * other file should index `accessControl.dimensions` directly. It is `null`
 * whenever there is no enforcement to apply: the response has not arrived yet,
 * the BE predates `accessControl`, or `dimensions` came back empty.
 *
 * `userId` is the actor's personnel entity id, and is likewise the single
 * accessor for it: the id lives at `data.entityData.id`, **not** at a
 * `data.id` sibling (the response's `data` carries `entityData` and `metadata`
 * and nothing else). Reaching in by hand is how DP-3188 first shipped a
 * dimension-scope storage key that silently never wrote — the empty id made
 * every key incomplete, so every write was skipped. It is `''` until `/me`
 * resolves; consumers that build storage keys must treat empty as "not ready"
 * rather than writing a partial key.
 */
/**
 * The dimension every deployment had before record-access existed.
 *
 * Teams were the only scope there was, so this is not a guessed default — it is
 * what the pre-NBAC dashboard hardcoded end to end (`DiscoTeamSelector`, the
 * `?teams=` param, `entityData.teamIds` filtering). Reinstating it when the
 * backend cannot describe itself is what keeps an un-upgraded client working
 * exactly as it did.
 */
const LEGACY_TEAM_DIMENSION = {
  name: 'team',
  recordTagField: 'teamIds',
} as const;

/**
 * Resolve the active dimension, tolerating a backend that predates NBAC.
 *
 * Three cases, and the middle one is the reason this is a function:
 * - `accessControl` **absent** → the backend cannot answer. Fall back to the
 *   legacy team dimension, carrying `allTeamsAccess` across as `allAccess`.
 * - `accessControl` present, `dimensions` **empty** → the backend answered
 *   "none". Honour it: enforcement is off for this deployment.
 * - `accessControl` present with a dimension → use it verbatim.
 */
function resolveActiveDimension(
  data: MeResponse['data'] | undefined
): AccessDimension | null {
  if (!data) {
    return null;
  }
  // Strictly *absent*, not merely falsy: `accessControl: null` is present but
  // malformed, and a truthiness check would read it as "old backend" and hand
  // back the legacy team dimension. Malformed data earns `null`.
  if (data.accessControl !== undefined) {
    return data.accessControl?.dimensions?.[0] ?? null;
  }
  return {
    ...LEGACY_TEAM_DIMENSION,
    // Old `/me` reports blanket team access here. Absent is treated as
    // unrestricted, matching how the pre-NBAC selector behaved.
    allAccess: data.entityData?.allTeamsAccess ?? true,
  };
}

export function useCurrentUser(clientId: string) {
  const { showApiError } = useToast();
  const query = useApiQuery<MeResponse>({
    key: ['me', clientId],
    endpointKey: 'me.get',
    params: { clientId },
    config: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      onError: (error: ApiError) => {
        showApiError(error);
      },
    },
  });

  // A reference into the cached response wherever possible, so downstream
  // `useMemo` dependencies stay stable for as long as the `/me` payload does.
  // The legacy branch necessarily builds an object; it is memoized on the
  // payload for the same reason.
  const activeDimension = useMemo(
    () => resolveActiveDimension(query.data?.data),
    [query.data]
  );

  const userId: string = query.data?.data?.entityData?.id ?? '';

  return { ...query, activeDimension, userId };
}
